import { UsersThree } from "@phosphor-icons/react";
import { type JSX, useState } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import SearchInput from "common/components/SearchInput";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { formatNumber } from "common/helpers/utils";
import useDebounce from "common/hooks/useDebounce";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useNavigatorLeads from "../../../../datahooks/useNavigatorLeads";
import useTargetingMutations from "../../../../datahooks/useTargetingMutations";
import SectionHeading from "../../../SectionHeading";
import StepperNextButton from "../../../StepperNextButton";
import Stepper from "../../Stepper";
import MinerAccountSelector from "../MinerAccountSelector";
import DesktopFilters from "./DesktopFilters";
import LeadsList from "./LeadsList";
import MobileFilters from "./MobileFilters";
import UrlTargeting from "./UrlTargeting";

import type {
  NavigatorFiltersTargeting,
  NavigatorUrlTargeting,
} from "../../../../types";

import ErrorPlaceholderImage from "assets/images/empty-placeholders/error.png";

const pageSize = 24;

export default function Navigator({
  initialTargeting,
}: {
  initialTargeting: NavigatorFiltersTargeting | NavigatorUrlTargeting;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { miner, outreach_type: outreachType, type } = useCampaignContext();
  const isDrip = outreachType === "drip";
  const { accounts } = useSelectedWorkspaceContext();
  const [inputValue, setInputValue] = useState("");
  const [keywords, setKeywords] = useDebounce(inputValue);
  const [selectedFilters, setSelectedFilters] = useState(
    (initialTargeting &&
      "filters" in initialTargeting &&
      initialTargeting.filters) ||
      [],
  );
  const {
    leads,
    filters,
    totalCount,
    isLoadingSalesNavigatorLeads,
    refetchNavigatorLeads,
    navigatorLeadsError,
  } = useNavigatorLeads({
    selectedFilters,
    keywords,
    pageSize,
  });
  const { isSettingTargeting, setTargeting } = useTargetingMutations();

  const searchAccount = accounts.find(({ id }) => id === miner);
  const isSearchAccountDisconnected = searchAccount.state !== "LoggedIn";
  const isSearchAccountNavigatorDisconnected = !searchAccount.sales_navigator;

  const FiltersComponent = isDesktop ? DesktopFilters : MobileFilters;
  const filtersComponent = (
    <FiltersComponent
      isLoading={isLoadingSalesNavigatorLeads}
      returnedFilters={filters}
      selectedFilters={selectedFilters}
      setSelectedFilters={setSelectedFilters}
    />
  );

  let content: JSX.Element;

  if (isSearchAccountNavigatorDisconnected) {
    content = (
      <EmptyPlaceholder
        title="Sales Navigator subscription expired"
        subtitle="The selected search account must have an active Sales Navigator subscription to find targets"
        imageSrc={ErrorPlaceholderImage}
      />
    );
  } else if (navigatorLeadsError) {
    content = (
      <ErrorPlaceholder
        errorMessage="Could not display leads data"
        onRetry={refetchNavigatorLeads}
      />
    );
  } else {
    content = (
      <div className="flex items-start gap-x-4">
        {isDesktop && filtersComponent}
        <div className="flex flex-1 flex-col gap-y-4">
          <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
            <div className="flex items-center justify-between gap-x-2">
              <SearchInput
                onClear={() => {
                  setKeywords("");
                  setInputValue("");
                }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Enter keyword"
                className="flex-1"
              />
              {!isDesktop && filtersComponent}
            </div>
            {!!totalCount && (
              <Tag
                variant="secondary"
                size="lg"
                leftIcon={<UsersThree weight="fill" />}
                className="ml-auto"
              >
                {formatNumber(totalCount)}
              </Tag>
            )}
          </div>
          <LeadsList
            leads={leads}
            isLoading={isLoadingSalesNavigatorLeads}
            pageSize={pageSize}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <SectionHeading
          title="Targeting"
          subtitle="Find targets for this campaign by adding relevant keywords and filters"
        />

        <div className="flex items-center gap-x-2.5">
          {(type === "navigator" || !isDrip) && (
            <>
              <div className="flex items-center gap-x-1">
                <span className="text-button-xs">Search account:</span>

                <MinerAccountSelector className="w-[130px]" size="sm" />
              </div>

              <div className="w-px self-stretch bg-black-200" />
            </>
          )}

          <UrlTargeting
            initialUrl={
              initialTargeting &&
              "url" in initialTargeting &&
              initialTargeting.url
            }
          />
        </div>
      </section>
      {content}
      <Stepper
        selectedIndex={0}
        nextButtonComponent={
          <StepperNextButton
            onClick={() =>
              setTargeting({
                campaignTargeting: { filters: selectedFilters, keywords },
              })
            }
            disabled={
              isSettingTargeting ||
              isSearchAccountDisconnected ||
              isSearchAccountNavigatorDisconnected
            }
            isLoading={isSettingTargeting}
          />
        }
      />
    </>
  );
}
